import { Skeleton } from "@/components/ui/alert"
import { cn } from "@/lib/utils"

import {
  AUTO,
  DETECTOR_SETTING_KEYS,
  SOURCE_LABEL,
  valueLabel,
  type DetectorProfile,
  type DetectorProfileOptions,
  type DetectorProfileResolved,
} from "./profile"

export interface DetectorProfileListProps {
  /** Undefined while the options load. */
  options: DetectorProfileOptions | undefined
  /** What the review ran with. Null before the run has resolved it. */
  resolved: DetectorProfileResolved | null
  /** What was asked for, shown while `resolved` is null. */
  requested?: DetectorProfile
  /**
   * Whether to say where each value came from. Staff vocabulary — an
   * engagement and a deployment default mean nothing to a client — so a
   * client-facing caller leaves it off.
   */
  showSource?: boolean
  className?: string
}

/**
 * The settings a review applied, one line each: "Typography: Emphasis (from
 * engagement)". Read-only.
 *
 * Before the run resolves them it shows what was asked for, and says that
 * Automatic is decided when the review runs rather than printing a value the
 * run may not use.
 */
export function DetectorProfileList({
  options,
  resolved,
  requested = {},
  showSource = false,
  className,
}: DetectorProfileListProps): JSX.Element {
  if (!options) {
    return (
      <div className={cn("space-y-2", className)} aria-busy="true">
        {DETECTOR_SETTING_KEYS.map((key) => (
          <Skeleton key={key} className="h-4 w-full" />
        ))}
      </div>
    )
  }

  return (
    <dl className={cn("space-y-1.5", className)}>
      {options.settings.map((setting) => {
        const value = resolved ? resolved.values[setting.key] : (requested[setting.key] ?? AUTO)
        const source = resolved?.source[setting.key]
        const pendingAuto = !resolved && value === AUTO
        // A run resolved before this setting existed recorded nothing for it.
        if (value === undefined) {
          return (
            <div key={setting.key} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-3">
              <dt className="text-caption text-text-3">{setting.label}</dt>
              <dd className="min-w-0 text-body-sm text-text-3">Not recorded</dd>
            </div>
          )
        }
        return (
          <div key={setting.key} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-3">
            <dt className="text-caption text-text-3">{setting.label}</dt>
            <dd className="min-w-0 text-body-sm text-text">
              {valueLabel(options, setting.key, value)}
              {showSource && source && (
                <span className="text-text-3"> ({SOURCE_LABEL[source]})</span>
              )}
              {showSource && pendingAuto && (
                <span className="text-text-3"> (decided when the review runs)</span>
              )}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}
